// useGithubAccount.ts
import {Database, supabase} from './supabase';

export type GithubAccount = Database['public']['Tables']['github_account']['Row'];

/**
 * Initiates the GitHub OAuth login flow by opening a new window.
 * @param userId The current authenticated user's ID from Supabase Auth. This will be used as the 'state' parameter.
 * @returns The new window object, or null if it could not be opened.
 */
export async function connectGithubAccount(userId: string): Promise<Window | null> {
    const GITHUB_OAUTH_REDIRECT = `${process.env.REACT_APP_SUPABASE_FUNCTIONS_URL}/github-oauth-callback`;
    const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID;

    const state = crypto.randomUUID(); // random, unguessable value

    if (!GITHUB_OAUTH_REDIRECT || !GITHUB_CLIENT_ID) {
      console.error('Missing GitHub OAuth environment variables.');
      return null;
    }

    // Store in session for comparison later
    sessionStorage.setItem('github_oauth_state', state);

    // Store state in database instead of sessionStorage
    const { error } = await supabase
        .from('oauth_states')
        .insert({
            user_id: userId,
            state_token: state,
            provider: 'github'
        })
        .select()
        .single();

    if (error) {
        console.error('Failed to store OAuth state:', error);
        return null;
    }

    const githubAuthUrl =
        `https://github.com/login/oauth/authorize` +
        `?client_id=${GITHUB_CLIENT_ID}` +
        `&redirect_uri=${encodeURIComponent(GITHUB_OAUTH_REDIRECT)}` +
        `&scope=repo,user` +
        `&state=${state}`;

     // Open in new tab
    return window.open(githubAuthUrl, '_blank');
}

/**
 * Creates a new GitHub account entry or updates an existing one for a user.
 * This acts as an upsert operation based on user_id.
 * @param userId The UUID of the authenticated user.
 * @param githubUserId The GitHub user ID.
 * @param githubUsername The GitHub username.
 * @param accessToken The GitHub access token.
 * @param refreshToken The GitHub refresh token.
 * @param expiresAt The timestamp when the access token expires (e.g., ISO string).
 * @returns The created or updated GithubAccount record.
 * @throws Error if the operation fails.
 */
export async function createOrUpdateGithubAccount(
  userId: string,
  githubUserId: number,
  githubUsername: string,
  accessToken: string,
  refreshToken: string | null,
  expiresAt: string | null // Can be Date object or ISO string
): Promise<GithubAccount> {
  const { data, error } = await supabase
    .from('github_account')
    .upsert(
      {
        user_id: userId,
        github_user_id: githubUserId,
        github_username: githubUsername,
        github_access_token: accessToken,
        github_refresh_token: refreshToken,
        token_expires_at: expiresAt,
        // 'updated_at' will be handled by a database trigger if configured.
        // 'created_at' will be handled by default value on insert.
      },
      {
        onConflict: 'user_id', // Conflict target for upsert
        ignoreDuplicates: false // Ensure update happens on conflict
      }
    )
    .select()
    .single();

  if (error) {
    console.error('Error creating or updating GitHub account:', error);
    throw new Error(`Failed to create or update GitHub account: ${error.message}`);
  }
  if (!data) {
    throw new Error('No data returned after creating or updating GitHub account.');
  }
  return data as GithubAccount;
}

/**
 * Retrieves a user's GitHub account details.
 * @param userId The UUID of the authenticated user.
 * @returns The GithubAccount record or null if not found.
 * @throws Error if the operation fails.
 */
export async function getGithubAccountByUserId(userId: string): Promise<GithubAccount | null> {
  try {
    const { data, error } = await supabase
      .from('github_account')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 means "no rows found"
      console.error('Error fetching GitHub account by user ID:', error);
      throw new Error(`Failed to fetch GitHub account: ${error.message}`);
    }

    return data as GithubAccount | null;
  } catch (err) {
    console.error('Exception in getGithubAccountByUserId:', err);
    throw err;
  }
}

/**
 * Refreshes the GitHub access token for a user by calling the Supabase Edge Function.
 * @param userId The UUID of the authenticated user.
 * @returns The updated GithubAccount record (with sensitive tokens omitted).
 * @throws Error if the operation fails.
 */
export async function refreshGithubAccountTokens(userId: string): Promise<Omit<GithubAccount, 'github_access_token' | 'github_refresh_token'>> {
  try {
    const session = await supabase.auth.getSession();
    const accessToken = session.data.session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const response = await fetch(`${process.env.REACT_APP_SUPABASE_FUNCTIONS_URL}/github-refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Authorization header might be needed if the edge function is protected by RLS
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error calling github-refresh-token edge function:', errorData);
      throw new Error(`Failed to refresh GitHub account tokens via edge function: ${errorData.message || 'Unknown error'}`);
    }

    const { updatedAccount } = await response.json();
    return updatedAccount;
  } catch (err) {
    console.error('Exception in refreshGithubAccountTokens (via edge function):', err);
    throw err;
  }
}

/**
 * Deletes a user's GitHub account entry.
 * @param userId The UUID of the authenticated user whose GitHub account to delete.
 * @returns True if deletion was successful, false otherwise.
 * @throws Error if the operation fails.
 */
export async function deleteGithubAccount(userId: string): Promise<boolean> {
  try {
    const { error, count } = await supabase
      .from('github_account')
      .delete()
      .eq('user_id', userId)
      .select(); // Use select() to get count of deleted rows

    if (error) {
      console.error('Error deleting GitHub account:', error);
      throw new Error(`Failed to delete GitHub account: ${error.message}`);
    }
    // Supabase delete() returns count of deleted rows if select() is used.
    // If no rows were found to delete, count will be 0.
    return (count || 0) > 0;
  } catch (err) {
    console.error('Exception in deleteGithubAccount:', err);
    throw err;
  }
}

/**
 * Checks if a given GitHub token expiration timestamp is in the past.
 * @param expiresAtTimestamp The timestamp (ISO string or Date object) when the token expires.
 * @returns True if the token has expired, false otherwise.
 */
export function isGithubTokenExpired(expiresAtTimestamp: string | Date | null): boolean {
  if (!expiresAtTimestamp) {
    return true; // Consider a missing expiration as expired or invalid
  }
  const expirationDate = new Date(expiresAtTimestamp);
  return expirationDate.getTime() <= Date.now();
}

/**
 * Ensures the user's GitHub access token is valid, refreshing it if expired.
 * @param userId The UUID of the authenticated user.
 * @returns The updated GithubAccount record.
 * @throws Error if the GitHub account is not found or if token refresh fails.
 */
export async function checkAndUpdateGithubToken(userId: string): Promise<GithubAccount> {
  // console.log('Checking GitHub token validity for user:', userId);
  let githubAccount = await getGithubAccountByUserId(userId);

  if (!githubAccount) {
    throw new Error('GitHub account not linked for this user.');
  }

  if (isGithubTokenExpired(githubAccount.token_expires_at)) {
    // console.log('GitHub token expired, attempting to refresh...');
    await refreshGithubAccountTokens(userId);
    githubAccount = await getGithubAccountByUserId(userId);
    
    if (!githubAccount) {
      // This case should ideally not happen if refresh was successful, but for safety
      throw new Error('Failed to retrieve GitHub account after token refresh.');
    }
    // console.log('GitHub token refreshed successfully.');
  } else {
    // console.log('GitHub token is valid.');
  }

  return githubAccount;
}